<script lang="ts">
  import SlideLocalWrapper from 'Components/Common/Transitions/SlideLocalWrapper.svelte';
  import {
    amounts,
    deal,
    isLessThanMinInvestment,
    isMoreThanMaxInvestment,
    patchUpdateInvestor,
    personalInfo,
    isPresetSharesExperimentEnabled,
  } from 'Store/InvestorFlowStore';
  import {whiteLabeling} from 'Store/WhiteLabelStore';
  import {dollarMask} from 'Utility/formatters/mask';
  import {numberOfSecurities} from 'Utility/investorFlow/v2/amountSelection';
  import {centsToString} from 'Utility/formatters/currency';
  import type {CssColor} from 'Catalyst/utility/theme';

  import Form from 'Components/Common/Form/Form.svelte';
  import SwitchVertical from 'Components/Common/icons/Switch.svelte';
  import MoneyField from 'Components/InvestorFlow/v2/Components/LeftContainer/AmountFields/MoneyField.svelte';
  import SharesField from 'Components/InvestorFlow/v2/Components/LeftContainer/AmountFields/SharesField.svelte';
  import PresetAmount from 'Components/InvestorFlow/v2/Components/LeftContainer/AmountFields/PresetAmount.svelte';
  import Button from 'Catalyst/components/Button/Button.svelte';
  import CatalystApp from 'Catalyst/components/CatalystApp/CatalystApp.svelte';
  import CatalystStyleOverwrite from 'Components/InvestorFlow/CatalystStyleOverwrite.svelte';

  export let minimum_investment: number; // In cents
  export let maximum_investment: number; // In cents
  export let price_per_security: number; // In cents
  export let currency: string;
  export let customInvestmentAmount: number = $amounts[0] || 0; // In cents

  let numSecurities: number = numberOfSecurities(
    Number(customInvestmentAmount),
    price_per_security
  );
  let exchangePrice: boolean = !isPresetSharesExperimentEnabled();
  let moreThanMax: boolean = false;
  let maskedValue: string = String(customInvestmentAmount / 100); // In dollars for display purpose
  let lessThanMin: boolean = false;
  let firstMount: boolean = true;
  $: hideUnitConverter = $deal?.feature_flags?.hide_flow_unit_converter;

  const handlerExchangePrice = () => {
    exchangePrice = !exchangePrice;
  };

  const updateNumberOfSecurities = (): void => {
    const updatedSecurities = numberOfSecurities(
      Number(maskedValue) * 100,
      price_per_security
    );

    numSecurities = updatedSecurities;
    customInvestmentAmount = numSecurities * price_per_security;
  };

  const onMoneyClickOrBlur = (amount: number) => {
    // Update the value displayed on blur for input
    maskedValue = String(amount / 100);
    updateNumberOfSecurities();
  };

  const onHandleMoneyInput = (): void => {
    updateNumberOfSecurities();
    firstMount = false;
  };

  $: existingMinimumInvestment =
    $deal.minimum_investment !== 0 && $deal.minimum_investment !== null;

  $: lessThanMin = isLessThanMinInvestment(
    customInvestmentAmount,
    minimum_investment
  );

  $: moreThanMax = isMoreThanMaxInvestment(
    customInvestmentAmount,
    maximum_investment
  );

  $: emptyDealPresets =
    $deal.preset_amount.primary === null || $deal.preset_amount.primary === 0;

  $: canShowMinimumError =
    (emptyDealPresets && !firstMount) || !emptyDealPresets;

  $: customAmountRounded = Math.ceil(customInvestmentAmount) / 100;

  $: disableSendInvitationBtn = !(
    $personalInfo.investment_amount >= $deal.minimum_investment &&
    !isMoreThanMaxInvestment(
      $personalInfo.investment_amount,
      $deal.maximum_investment
    ) &&
    $personalInfo.investment_amount > 0
  );

  $: if (customInvestmentAmount) {
    $personalInfo.investment_amount = customInvestmentAmount;
  }

  $: brandColor = $whiteLabeling.primary_color as CssColor;

  updateNumberOfSecurities();
  const submitInvestor = async (): Promise<void> => {
    await patchUpdateInvestor();
  };
  const mappings = {
    '--button-variants-style-filled-surface-default': '--dm-bg-secondary-color',
    '--button-variants-style-filled-surface-hover': '--dm-bg-secondary-color',
    '--button-variants-style-filled-surface-focus': '--dm-bg-secondary-color',
    '--button-variants-style-filled-surface-active': '--dm-bg-secondary-color',
    '--button-variants-style-filled-on-surface-default':
      '--dm-text-primary-color',
    '--button-base-border-radius': '9999px',
  };
</script>

<CatalystApp {brandColor}>
  <Form on:submit={submitInvestor}>
    <div
      class="amount-container"
      style:--primary-color={brandColor}
      data-testid="amount-selection"
    >
      <div class="section-heading">
        <div>How much would you like to invest?</div>
      </div>
      <div class="flex justify-center">
        {#if exchangePrice}
          <div
            class="flex gap-2 items-center"
            data-testid="custom-amount-button"
          >
            <MoneyField
              bind:maskedValue
              {numSecurities}
              {customInvestmentAmount}
              {hideUnitConverter}
              forceDecimals={false}
              on:input={() => setTimeout(onHandleMoneyInput)}
            />

            {#if !hideUnitConverter}
              <CatalystStyleOverwrite {mappings}>
                <Button
                  class="rounded-lg"
                  data-testid="switch-button"
                  on:click={() => handlerExchangePrice()}
                  on:keydown={() => handlerExchangePrice()}
                  type="button"
                >
                  <SwitchVertical />
                </Button>
              </CatalystStyleOverwrite>
            {/if}
          </div>
        {:else}
          <div
            class="flex gap-2 items-center"
            data-testid="shares-input-container"
          >
            <SharesField
              bind:customInvestmentAmount
              bind:maskedValue
              bind:numSecurities
              {price_per_security}
            />
            <CatalystStyleOverwrite {mappings}>
              <Button
                class="rounded-lg"
                data-testid="switch-button"
                on:click={() => handlerExchangePrice()}
                on:keydown={() => handlerExchangePrice()}
                type="button"
              >
                <SwitchVertical />
              </Button>
            </CatalystStyleOverwrite>
          </div>
        {/if}
      </div>
      {#if lessThanMin && canShowMinimumError}
        <SlideLocalWrapper>
          <div
            class="invalid-amount-message"
            data-testid="minimum-amount-reached-message"
          >
            Please enter at least the minimum investment required.
          </div>
        </SlideLocalWrapper>
      {/if}
      {#if existingMinimumInvestment}
        <div
          class="invalid-amount-message alert-message"
          data-testid="minimum-investment-amount-message"
        >
          Minimum investment {$deal.currency_symbol}{centsToString(
            $deal.minimum_investment
          )}
        </div>
      {/if}
      {#if moreThanMax}
        <SlideLocalWrapper>
          <div
            class="invalid-amount-message alert-message"
            data-testid="maximum-amount-reached-message"
          >
            Maximum investment amount is {currency}
            {dollarMask(maximum_investment / 100, $deal.currency_symbol)}.
          </div>
        </SlideLocalWrapper>
      {:else if exchangePrice && customAmountRounded !== Number(maskedValue)}
        <div
          class="invalid-amount-message alert-message"
          data-testid="invalid-amount-message"
        >
          Your investment amount will be rounded up to <b class="amount"
            >{currency}
            {dollarMask(customAmountRounded, $deal.currency_symbol)}</b
          >
        </div>
      {/if}
      <div class="amounts" data-testid="preset-amount-input">
        {#each $amounts as amount}
          <PresetAmount
            currencySymbol={$deal.currency_symbol}
            {amount}
            pricePerSecurity={price_per_security}
            {numSecurities}
            {hideUnitConverter}
            {exchangePrice}
            on:click={() => onMoneyClickOrBlur(amount)}
          />
        {/each}
      </div>
    </div>

    <div class="submit-button-container pt-6">
      <CatalystStyleOverwrite>
        <Button
          label="Continue"
          trailingIcon="arrow_forward"
          class="w-full"
          type="button"
          disabled={disableSendInvitationBtn}
          on:click={submitInvestor}
          data-testid="submit"
        />
      </CatalystStyleOverwrite>
    </div>
  </Form>
</CatalystApp>

<style lang="postcss">
  .invalid-amount-message {
    text-align: center;
    text-wrap: wrap;
    font-weight: 400;
    font-size: 1rem;
    line-height: 1.25rem;
    color: var(--dm-text-secondary-color, var(--palettes-neutral-575, #667085));
  }

  .alert-message {
    text-align: left;
    font-size: 0.875rem;
    padding-bottom: 0.5rem;
    padding-top: 0.5rem;
  }

  .amount-container {
    & .section-heading {
      font-weight: 600;
      font-size: 1.4rem;
      line-height: 2rem;
      color: var(--dm-text-primary-color, var(--palettes-neutral-950, #101828));
      margin-bottom: 1.2rem;
      text-align: center;
      text-transform: none;
    }
  }
  .amounts {
    display: flex;
    gap: 0.5rem;
    white-space: nowrap;
    margin-top: 1.2rem;
    align-items: stretch;
    flex-direction: column;
  }
</style>
